import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { pingTask } from '../../../util/apiService';
import { 
  closeMove, 
  setMoveTargetDocument, 
  setMovePreviewMode, 
  setMoveComment,
  setMoveOriginalData,
  setMoveNewData,
  setMoveLoading,
  setMovePosition,
  setMoveCreateCopy
} from '../../../redux/previewSlice';
import { getRevAllNodes } from '../../../redux/procedureRevisionSlice';
import { moveSection,moveChildnodes, copyElement } from '../../../redux/sectionSlice';
import { moveStep, moveSubstep } from '../../../redux/stepSlice';
import { showSnackbar } from '../../../redux/snackbarSlice';
import SelectRevisionTable from '../assets/components/SelectRevision/SelectRevisionTable';
import {
  calculateMoveParameters,
  prepareMoveNewContent,
  prepareCopyNewContent,
  buildCopyPayload,
  buildMovePayload,
} from './utils/sectionMoveCopyUtils';
import {
  calculateStepMoveParameters,
  prepareStepMoveNewContent,
  buildStepCopyPayload,
  buildStepMovePayload,
} from './utils/stepMoveCopyUtils';
import {
  calculateSubstepMoveParameters,
  prepareSubstepMoveNewContent,
  buildSubstepCopyPayload,
  buildSubstepMovePayload,
} from './utils/substepMoveCopyUtils';
import {
  calculateChildNodeMoveParameters,
  prepareChildNodeMoveNewContent,
  buildChildNodeCopyPayload,
  buildChildNodeMovePayload,
} from './utils/childNodeMoveCopyUtils';
import VersionHistoryCopyAppBar from '../../../common/VersionHistoryCopyAppBar';
import MoveReplacePreview from './components/MoveReplacePreview';
import MovePositionSelector from './components/MovePositionSelector';
import Loader from '../../../common/Loader';
import { getRevAndTraining, handleRedirectToCopiedNode, prepareCurrentSnapshot, uploadJsonToS3 } from '../../../util/AppUtils';

/**
 * MoveReplace Component
 * Main component for move functionality
 * Manages the flow: Select Target (if cross-doc) → Position Selection → Preview → Confirm
 * For same-document moves: Position Selection → Preview → Confirm
 */
const MoveReplace = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { 
    moveNode,
    moveDocument,
    moveTargetDocument,
    movePosition,
    movePreviewMode, 
    moveComment,
    moveOriginalData,
    moveNewData,
    moveIsLoading,
    moveCreateCopy
  } = useSelector(state => state.preview);
  
  const authUser = useSelector((state) => state.auth.user);
  
  const [step, setStep] = useState(null); // 'select' | 'position' | 'preview' - starts as null, determined by useEffect
  const [error, setError] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);

  // Determine if this is a cross-document move
  const isCrossDocumentMove = moveTargetDocument !== null;
  
  // Start ping interval for move document
  useEffect(() => {
    if (!moveDocument?.revisionId || !authUser) return;
    
    const interval = setInterval(async () => {
      try {
        const pingData = {
          task_id: moveDocument.revisionId,
          user_id: authUser.id
        };
        await pingTask(pingData);
      } catch (error) {
        console.error('Ping failed for move document in MoveReplace:', error);
      }
    }, 60000); // 60 seconds = 1 minute
    
    return () => {
      clearInterval(interval);
    };
  }, [moveDocument?.revisionId, authUser]);
  
  // Start ping interval for target document (if cross-document move)
  useEffect(() => {
    if (!moveTargetDocument?.revisionId || !authUser) return;
    // Only ping target if it's different from source
    if (moveTargetDocument.revisionId === moveDocument?.revisionId) return;
    
    const interval = setInterval(async () => {
      try {
        const pingData = {
          task_id: moveTargetDocument.revisionId,
          user_id: authUser.id
        };
        await pingTask(pingData);
      } catch (error) {
        console.error('Ping failed for target document in MoveReplace:', error);
      }
    }, 60000); // 60 seconds = 1 minute
    
    return () => {
      clearInterval(interval);
    };
  }, [moveTargetDocument?.revisionId, moveDocument?.revisionId, authUser]);

  // Initialize: Fetch original data and set initial step
  useEffect(() => {
    const fetchOriginalData = async () => {
      if (!moveDocument) return;
      
      try {
        setError(null);
        dispatch(setMoveLoading(true));
        
        // For same-doc move, fetch moveDocument data
        // For cross-doc move, we'll fetch target document data when selected
        const docToFetch = moveTargetDocument || moveDocument;
        const originalResponse = await dispatch(getRevAllNodes({ 
          partId: docToFetch.partId, 
          revisionId: docToFetch.revisionId 
        })).unwrap();
        
        // Clone original data to prevent mutations
        dispatch(setMoveOriginalData(structuredClone(originalResponse)));
        
        // Set initial step based on whether this is cross-document move
        if (isCrossDocumentMove) {
          setStep('select'); // Need to select target document
        } else {
          setStep('position'); // Same document, go directly to position selection
        }
      } catch (err) {
        console.error('Error fetching document data:', err);
        setError('Failed to load document data. Please try again.');
      } finally {
        dispatch(setMoveLoading(false));
      }
    };

    fetchOriginalData();
  }, [moveDocument, moveTargetDocument, dispatch, isCrossDocumentMove]);

  // Handle target document selection (for cross-document moves only)
  const handleRevisionSelect = async ({ group, revision }) => {
    try {
      setError(null);
      dispatch(setMoveLoading(true));
      
      const targetDoc = {
        partId: group.partId,
        revisionId: revision.id,
        partNumber: group.partNumber,
        revision: revision.label,
        type: group.type,
        description: group.description,
        status: group.status || 'Not Submitted'
      };
      
      dispatch(setMoveTargetDocument(targetDoc));
      
      // Fetch target document data
      const originalResponse = await dispatch(getRevAllNodes({ 
        partId: targetDoc.partId, 
        revisionId: targetDoc.revisionId 
      })).unwrap();
      
      // Clone original data to prevent mutations
      dispatch(setMoveOriginalData(structuredClone(originalResponse)));
      
      // Go to position selection
      setStep('position');
    } catch (err) {
      console.error('Error fetching document data:', err);
      setError('Failed to load document data. Please try again.');
    } finally {
      dispatch(setMoveLoading(false));
    }
  };

  // Handle preview mode toggle
  const handlePreviewModeChange = (mode) => {
    dispatch(setMovePreviewMode(mode));
  };

  // Handle comment change
  const handleCommentChange = (newComment) => {
    dispatch(setMoveComment(newComment));
  };

  // Handle create copy checkbox change
  const handleCreateCopyChange = (value) => {
    dispatch(setMoveCreateCopy(value));
  };

  // Handle position selection from MovePositionSelector
  const handlePositionSelected = async (position, targetType, targetId, targetIndex) => {
    try {
      dispatch(setMoveLoading(true));
      
      // The document we're moving within/to
      const targetDoc = moveTargetDocument || moveDocument;
      
      // Fetch source document data to get the node to move
      const sourceResponse = await dispatch(getRevAllNodes({ 
        partId: moveDocument.partId, 
        revisionId: moveDocument.revisionId 
      })).unwrap();
      
      // Prepare working copy of target data (use structuredClone to ensure complete independence)
      // We'll work directly on sortedSections and clone it at the end
      const sortedSections = structuredClone(moveOriginalData || []).sort((a, b) => {
        const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
        const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
        return orderA - orderB;
      });
      
      // Track that we'll assign this at the end
      let newTargetData = null;

      // Find the source node (section)
      if (moveNode.nodeType === 'section') {
        const sourceSection = sourceResponse.find(
          section => section.tree_node_id === moveNode.nodeId
        );
        
        if (!sourceSection) {
          dispatch(showSnackbar({ message: 'Source section not found', type: 'error' }));
          return;
        }

        // Clone the section to move
        const clonedSection = JSON.parse(JSON.stringify(sourceSection));

        // Compute new data based on position
        if (position === 'into') {
          // For same-document MOVES (not copies), remove the source section from its original position
          const isSameDocument = moveDocument.partId === targetDoc.partId && moveDocument.revisionId === targetDoc.revisionId;
          if (isSameDocument && !moveCreateCopy) {
            const sourceIndex = sortedSections.findIndex(
              s => s.tree_node_id === moveNode.nodeId
            );
            if (sourceIndex !== -1) {
              // Remove from original position (only for moves, not copies)
              sortedSections.splice(sourceIndex, 1);
            }
          }
          
          // Move/copy to end
          const maxOrder = sortedSections.length > 0 
            ? parseInt(sortedSections[sortedSections.length - 1].sibling_order || sortedSections[sortedSections.length - 1].procedure_tree?.sibling_order || '0', 10)
            : 0;
          clonedSection.sibling_order = (maxOrder + 1).toString();
          sortedSections.push(clonedSection);
          sortedSections.forEach((section, index) => {
            section.sibling_order = index.toString();
          });
          newTargetData = sortedSections;
        } else if (position === 'above') {
          // Move/copy above target section
          const targetSectionIndex = sortedSections.findIndex(
            section => section.tree_node_id === targetId
          );
          
          if (targetSectionIndex !== -1) {
            // For same-document MOVES (not copies), remove from original position
            const isSameDocument = moveDocument.partId === targetDoc.partId && moveDocument.revisionId === targetDoc.revisionId;
            const sourceIndex = sortedSections.findIndex(
              s => s.tree_node_id === moveNode.nodeId
            );
            
            if (sourceIndex !== -1 && isSameDocument && !moveCreateCopy) {
              // Remove from original position (only for moves, not copies)
              sortedSections.splice(sourceIndex, 1);
              // Adjust target index if needed
              if (sourceIndex < targetSectionIndex) {
                // Source was before target, target index shifts down
                sortedSections.splice(targetSectionIndex - 1, 0, clonedSection);
              } else {
                // Source was after target
                sortedSections.splice(targetSectionIndex, 0, clonedSection);
              }
            } else {
              // Copy operation or cross-document move: just insert (don't remove source)
              sortedSections.splice(targetSectionIndex, 0, clonedSection);
            }
            
            // Reassign sibling orders
            sortedSections.forEach((section, index) => {
              section.sibling_order = index.toString();
            });
            newTargetData = sortedSections;
          }
        } else if (position === 'below') {
          // Move/copy below target section
          const targetSectionIndex = sortedSections.findIndex(
            section => section.tree_node_id === targetId
          );
          
          if (targetSectionIndex !== -1) {
            // For same-document MOVES (not copies), remove from original position
            const isSameDocument = moveDocument.partId === targetDoc.partId && moveDocument.revisionId === targetDoc.revisionId;
            const sourceIndex = sortedSections.findIndex(
              s => s.tree_node_id === moveNode.nodeId
            );
            
            if (sourceIndex !== -1 && isSameDocument && !moveCreateCopy) {
              // Remove from original position (only for moves, not copies)
              sortedSections.splice(sourceIndex, 1);
              // Adjust target index if needed
              if (sourceIndex < targetSectionIndex) {
                // Source was before target
                sortedSections.splice(targetSectionIndex, 0, clonedSection);
              } else {
                // Source was after target
                sortedSections.splice(targetSectionIndex + 1, 0, clonedSection);
              }
            } else {
              // Copy operation or cross-document move: just insert (don't remove source)
              sortedSections.splice(targetSectionIndex + 1, 0, clonedSection);
            }
            
            // Reassign sibling orders
            sortedSections.forEach((section, index) => {
              section.sibling_order = index.toString();
            });
            newTargetData = sortedSections;
          }
        }
      } else if (moveNode.nodeType === 'step') {
        // Handle step move/copy
        // Find source section and step
        let sourceStep = null;
        let sourceSection = null;
        
        for (const section of sourceResponse) {
          sourceStep = section.steps?.find(
            step => step.tree_node_id === moveNode.nodeId || 
                    step.step_id_ref === moveNode.nodeId
          );
          if (sourceStep) {
            sourceSection = section;
            break;
          }
        }
        
        if (!sourceStep || !sourceSection) {
          dispatch(showSnackbar({ message: 'Source step not found', type: 'error' }));
          return;
        }

        // Clone the step to move
        const clonedStep = JSON.parse(JSON.stringify(sourceStep));

        // Compute new data based on position
        if (position === 'into') {
          // Move/copy step INTO a section (at the end)
          const targetSection = sortedSections.find(
            section => section.tree_node_id === targetId || 
                       section.section_id_ref === targetId
          );
          
          if (!targetSection) {
            dispatch(showSnackbar({ message: 'Target section not found', type: 'error' }));
            return;
          }

          // For same-document MOVES (not copies), remove the source step from its original position
          const isSameDocument = moveDocument.partId === targetDoc.partId && moveDocument.revisionId === targetDoc.revisionId;
          const isSameSection = sourceSection.section_id_ref === targetSection.section_id_ref;
          
          if (isSameDocument && !moveCreateCopy && isSameSection && targetSection.steps) {
            // Remove from original position (only for same-section moves, not copies)
            // Ensure steps array is mutable by creating a copy
            targetSection.steps = [...targetSection.steps];
            const sourceStepIndex = targetSection.steps.findIndex(
              s => s.tree_node_id === sourceStep.tree_node_id || 
                   s.step_id_ref === sourceStep.step_id_ref
            );
            if (sourceStepIndex !== -1) {
              targetSection.steps.splice(sourceStepIndex, 1);
            }
          } else if (isSameDocument && !moveCreateCopy && !isSameSection) {
            // Cross-section move: remove from source section
            const sourceSectionInTarget = sortedSections.find(
              s => s.section_id_ref === sourceSection.section_id_ref
            );
            if (sourceSectionInTarget && sourceSectionInTarget.steps) {
              // Ensure steps array is mutable by creating a copy
              sourceSectionInTarget.steps = [...sourceSectionInTarget.steps];
              const sourceStepIndex = sourceSectionInTarget.steps.findIndex(
                s => s.tree_node_id === sourceStep.tree_node_id || 
                     s.step_id_ref === sourceStep.step_id_ref
              );
              if (sourceStepIndex !== -1) {
                sourceSectionInTarget.steps.splice(sourceStepIndex, 1);
              }
            }
          }

          // Add to target section's steps array
          // Ensure steps array is mutable
          if (!targetSection.steps) {
            targetSection.steps = [];
          } else {
            targetSection.steps = [...targetSection.steps];
          }
          targetSection.steps.push(clonedStep);
          
          newTargetData = sortedSections;
        } else if (position === 'above' || position === 'below') {
          // Move/copy step ABOVE/BELOW another step
          let targetStep = null;
          let targetSection = null;
          
          // Find target step
          for (const section of sortedSections) {
            targetStep = section.steps?.find(
              step => step.tree_node_id === targetId || 
                      step.step_id_ref === targetId
            );
            if (targetStep) {
              targetSection = section;
              break;
            }
          }
          
          if (!targetStep || !targetSection) {
            dispatch(showSnackbar({ message: 'Target step not found', type: 'error' }));
            return;
          }

          const isSameDocument = moveDocument.partId === targetDoc.partId && moveDocument.revisionId === targetDoc.revisionId;
          const isSameSection = sourceSection.section_id_ref === targetSection.section_id_ref;

          // Find target step index
          const targetStepIndex = targetSection.steps?.findIndex(
            step => step.tree_node_id === targetId || 
                    step.step_id_ref === targetId
          );

          if (targetStepIndex !== -1) {
            // Ensure target section's steps array is mutable
            if (!targetSection.steps) {
              targetSection.steps = [];
            } else {
              targetSection.steps = [...targetSection.steps];
            }
            
            if (isSameDocument && !moveCreateCopy && isSameSection) {
              // Same-section move: remove from source position first
              const sourceStepIndex = targetSection.steps.findIndex(
                s => s.tree_node_id === sourceStep.tree_node_id || 
                     s.step_id_ref === sourceStep.step_id_ref
              );
              
              if (sourceStepIndex !== -1) {
                targetSection.steps.splice(sourceStepIndex, 1);
                
                // Adjust target index if needed
                let adjustedTargetIndex = targetStepIndex;
                if (sourceStepIndex < targetStepIndex) {
                  adjustedTargetIndex = targetStepIndex - 1;
                }
                
                // Insert at adjusted position
                if (position === 'above') {
                  targetSection.steps.splice(adjustedTargetIndex, 0, clonedStep);
                } else {
                  targetSection.steps.splice(adjustedTargetIndex + 1, 0, clonedStep);
                }
              } else {
                // Source not found, just insert
                if (position === 'above') {
                  targetSection.steps.splice(targetStepIndex, 0, clonedStep);
                } else {
                  targetSection.steps.splice(targetStepIndex + 1, 0, clonedStep);
                }
              }
            } else if (isSameDocument && !moveCreateCopy && !isSameSection) {
              // Cross-section move: remove from source, insert at target
              const sourceSectionInTarget = sortedSections.find(
                s => s.section_id_ref === sourceSection.section_id_ref
              );
              if (sourceSectionInTarget && sourceSectionInTarget.steps) {
                // Ensure source section's steps array is mutable
                sourceSectionInTarget.steps = [...sourceSectionInTarget.steps];
                const sourceStepIndex = sourceSectionInTarget.steps.findIndex(
                  s => s.tree_node_id === sourceStep.tree_node_id || 
                       s.step_id_ref === sourceStep.step_id_ref
                );
                if (sourceStepIndex !== -1) {
                  sourceSectionInTarget.steps.splice(sourceStepIndex, 1);
                }
              }
              
              // Insert at target position
              if (position === 'above') {
                targetSection.steps.splice(targetStepIndex, 0, clonedStep);
              } else {
                targetSection.steps.splice(targetStepIndex + 1, 0, clonedStep);
              }
            } else {
              // Copy operation or cross-document move: just insert (don't remove source)
              if (position === 'above') {
                targetSection.steps.splice(targetStepIndex, 0, clonedStep);
              } else {
                targetSection.steps.splice(targetStepIndex + 1, 0, clonedStep);
              }
            }
          }
          
          newTargetData = sortedSections;
        }
      } else if (moveNode.nodeType === 'substep') {
        // Handle substep move/copy
        // Find source section, step, and substep
        let sourceSubstep = null;
        let sourceStep = null;
        let sourceSection = null;
        
        for (const section of sourceResponse) {
          for (const step of section.steps || []) {
            sourceSubstep = step.substeps?.find(
              substep => substep.tree_node_id === moveNode.nodeId || 
                         substep.substep_id_ref === moveNode.nodeId
            );
            if (sourceSubstep) {
              sourceStep = step;
              sourceSection = section;
              break;
            }
          }
          if (sourceSubstep) break;
        }
        
        if (!sourceSubstep || !sourceStep || !sourceSection) {
          dispatch(showSnackbar({ message: 'Source substep not found', type: 'error' }));
          return;
        }

        // Clone the substep to move
        const clonedSubstep = JSON.parse(JSON.stringify(sourceSubstep));

        // Compute new data based on position
        if (position === 'into') {
          // Move/copy substep INTO a step (at the end of substeps)
          let targetStep = null;
          let targetSection = null;
          
          // Find target step
          for (const section of sortedSections) {
            targetStep = section.steps?.find(
              step => step.tree_node_id === targetId || 
                     step.step_id_ref === targetId
            );
            if (targetStep) {
              targetSection = section;
              break;
            }
          }
          
          if (!targetStep) {
            dispatch(showSnackbar({ message: 'Target step not found', type: 'error' }));
            return;
          }

          // For same-document MOVES (not copies), remove the source substep from its original position
          const isSameDocument = moveDocument.partId === targetDoc.partId && moveDocument.revisionId === targetDoc.revisionId;
          const isSameStep = sourceStep.step_id_ref === targetStep.step_id_ref;
          
          if (isSameDocument && !moveCreateCopy && isSameStep && targetStep.substeps) {
            // Remove from original position (only for same-step moves, not copies)
            // Ensure substeps array is mutable by creating a copy
            targetStep.substeps = [...targetStep.substeps];
            const sourceSubstepIndex = targetStep.substeps.findIndex(
              s => s.tree_node_id === sourceSubstep.tree_node_id || 
                   s.substep_id_ref === sourceSubstep.substep_id_ref
            );
            if (sourceSubstepIndex !== -1) {
              targetStep.substeps.splice(sourceSubstepIndex, 1);
            }
          } else if (isSameDocument && !moveCreateCopy && !isSameStep) {
            // Cross-step move: remove from source step
            const sourceSectionInTarget = sortedSections.find(
              s => s.section_id_ref === sourceSection.section_id_ref
            );
            const sourceStepInTarget = sourceSectionInTarget?.steps?.find(
              step => step.step_id_ref === sourceStep.step_id_ref
            );
            if (sourceStepInTarget && sourceStepInTarget.substeps) {
              // Ensure substeps array is mutable by creating a copy
              sourceStepInTarget.substeps = [...sourceStepInTarget.substeps];
              const sourceSubstepIndex = sourceStepInTarget.substeps.findIndex(
                s => s.tree_node_id === sourceSubstep.tree_node_id || 
                     s.substep_id_ref === sourceSubstep.substep_id_ref
              );
              if (sourceSubstepIndex !== -1) {
                sourceStepInTarget.substeps.splice(sourceSubstepIndex, 1);
              }
            }
          }

          // Add to target step's substeps array
          // Ensure substeps array is mutable
          if (!targetStep.substeps) {
            targetStep.substeps = [];
          } else {
            targetStep.substeps = [...targetStep.substeps];
          }
          targetStep.substeps.push(clonedSubstep);
          
          newTargetData = sortedSections;
        } else if (position === 'above' || position === 'below') {
          // Move/copy substep ABOVE/BELOW another substep
          let targetSubstep = null;
          let targetStep = null;
          let targetSection = null;
          
          // Find target substep
          for (const section of sortedSections) {
            for (const step of section.steps || []) {
              targetSubstep = step.substeps?.find(
                substep => substep.tree_node_id === targetId || 
                           substep.substep_id_ref === targetId
              );
              if (targetSubstep) {
                targetStep = step;
                targetSection = section;
                break;
              }
            }
            if (targetSubstep) break;
          }
          
          if (!targetSubstep || !targetStep || !targetSection) {
            dispatch(showSnackbar({ message: 'Target substep not found', type: 'error' }));
            return;
          }

          const isSameDocument = moveDocument.partId === targetDoc.partId && moveDocument.revisionId === targetDoc.revisionId;
          const isSameStep = sourceStep.step_id_ref === targetStep.step_id_ref;

          // Find target substep index
          const targetSubstepIndex = targetStep.substeps?.findIndex(
            substep => substep.tree_node_id === targetId || 
                       substep.substep_id_ref === targetId
          );

          if (targetSubstepIndex !== -1) {
            // Ensure target step's substeps array is mutable
            if (!targetStep.substeps) {
              targetStep.substeps = [];
            } else {
              targetStep.substeps = [...targetStep.substeps];
            }
            
            if (isSameDocument && !moveCreateCopy && isSameStep) {
              // Same-step move: remove from source position first
              const sourceSubstepIndex = targetStep.substeps.findIndex(
                s => s.tree_node_id === sourceSubstep.tree_node_id || 
                     s.substep_id_ref === sourceSubstep.substep_id_ref
              );
              
              if (sourceSubstepIndex !== -1) {
                targetStep.substeps.splice(sourceSubstepIndex, 1);
                
                // Adjust target index if needed
                let adjustedTargetIndex = targetSubstepIndex;
                if (sourceSubstepIndex < targetSubstepIndex) {
                  adjustedTargetIndex = targetSubstepIndex - 1;
                }
                
                // Insert at adjusted position
                if (position === 'above') {
                  targetStep.substeps.splice(adjustedTargetIndex, 0, clonedSubstep);
                } else {
                  targetStep.substeps.splice(adjustedTargetIndex + 1, 0, clonedSubstep);
                }
              } else {
                // Source not found, just insert
                if (position === 'above') {
                  targetStep.substeps.splice(targetSubstepIndex, 0, clonedSubstep);
                } else {
                  targetStep.substeps.splice(targetSubstepIndex + 1, 0, clonedSubstep);
                }
              }
            } else if (isSameDocument && !moveCreateCopy && !isSameStep) {
              // Cross-step move: remove from source, insert at target
              const sourceSectionInTarget = sortedSections.find(
                s => s.section_id_ref === sourceSection.section_id_ref
              );
              const sourceStepInTarget = sourceSectionInTarget?.steps?.find(
                step => step.step_id_ref === sourceStep.step_id_ref
              );
              if (sourceStepInTarget && sourceStepInTarget.substeps) {
                // Ensure source step's substeps array is mutable
                sourceStepInTarget.substeps = [...sourceStepInTarget.substeps];
                const sourceSubstepIndex = sourceStepInTarget.substeps.findIndex(
                  s => s.tree_node_id === sourceSubstep.tree_node_id || 
                       s.substep_id_ref === sourceSubstep.substep_id_ref
                );
                if (sourceSubstepIndex !== -1) {
                  sourceStepInTarget.substeps.splice(sourceSubstepIndex, 1);
                }
              }
              
              // Insert at target position
              if (position === 'above') {
                targetStep.substeps.splice(targetSubstepIndex, 0, clonedSubstep);
              } else {
                targetStep.substeps.splice(targetSubstepIndex + 1, 0, clonedSubstep);
              }
            } else {
              // Copy operation or cross-document move: just insert (don't remove source)
              if (position === 'above') {
                targetStep.substeps.splice(targetSubstepIndex, 0, clonedSubstep);
              } else {
                targetStep.substeps.splice(targetSubstepIndex + 1, 0, clonedSubstep);
              }
            }
          }
          
          newTargetData = sortedSections;
        }
      } else if (['attachment', 'video', 'rich-text', 'form', 'canva', 'canvas'].includes(moveNode.nodeType)) {
        // Handle child node (attachment, video, rich-text, canvas, form) move/copy
        const childNodeType = moveNode.nodeType === 'canva' ? 'canvas' : moveNode.nodeType;
        
        // Get the node ID key based on child node type
        const getNodeIdKey = (type) => {
          const keyMap = {
            'attachment': 'attachment_tree_node_id',
            'video': 'video_tree_node_id',
            'rich-text': 'rich_text_node_id',
            'canvas': 'canvas_tree_node_id',
            'form': 'form_tree_node_id',
          };
          return keyMap[type] || 'tree_node_id';
        };

        const nodeIdKey = getNodeIdKey(childNodeType);
        
        // Find source child node
        let sourceChildNode = null;
        let sourceParent = null; // Step or substep
        let sourceSection = null;
        
        // Search through all sections, steps, and substeps
        for (const section of sourceResponse) {
          for (const step of section.steps || []) {
            // Check step-level children
            sourceChildNode = step.children?.find(child => {
              const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
              return childId === moveNode.nodeId || child.tree_node_id === moveNode.nodeId;
            });
            if (sourceChildNode) {
              sourceParent = step;
              sourceSection = section;
              break;
            }

            // Check substep-level children
            for (const substep of step.substeps || []) {
              sourceChildNode = substep.children?.find(child => {
                const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
                return childId === moveNode.nodeId || child.tree_node_id === moveNode.nodeId;
              });
              if (sourceChildNode) {
                sourceParent = substep;
                sourceSection = section;
                break;
              }
            }
            if (sourceChildNode) break;
          }
          if (sourceChildNode) break;
        }
        
        if (!sourceChildNode || !sourceParent || !sourceSection) {
          dispatch(showSnackbar({ message: 'Source child node not found', type: 'error' }));
          return;
        }

        // Clone the child node to move (use structuredClone to preserve all properties)
        const clonedChildNode = structuredClone(sourceChildNode);

        // Compute new data based on position
        if (position === 'into') {
          // Move/copy child node INTO a step or substep
          if (targetType === 'step') {
            // Target is a step
            // Find the section containing the target step
            let targetSection = null;
            let targetStep = null;
            
            for (const section of sortedSections) {
              targetStep = (section.steps || []).find(
                step => step.tree_node_id === targetId || step.step_id_ref === targetId
              );
              if (targetStep) {
                targetSection = section;
                break;
              }
            }
            
            if (!targetStep || !targetSection) {
              dispatch(showSnackbar({ message: 'Target step not found', type: 'error' }));
              return;
            }

            // Ensure section's steps array is mutable
            if (!targetSection.steps) {
              targetSection.steps = [];
            } else {
              targetSection.steps = [...targetSection.steps];
            }
            
            // Find the step index in the section's steps array and update the reference
            const targetStepIndex = targetSection.steps.findIndex(
              step => step.tree_node_id === targetStep.tree_node_id || step.step_id_ref === targetStep.step_id_ref
            );
            if (targetStepIndex !== -1) {
              targetStep = targetSection.steps[targetStepIndex];
            }

            // For same-document MOVES (not copies), remove the source child node from its original position
            const isSameDocument = moveDocument.partId === targetDoc.partId && moveDocument.revisionId === targetDoc.revisionId;
            const isSameStep = sourceParent === targetStep || (sourceParent.step_id_ref === targetStep.step_id_ref && !sourceParent.substep_id_ref);
            
            // Ensure target step's children array is mutable (do this once, before any operations)
            if (!targetStep.children) {
              targetStep.children = [];
            } else {
              targetStep.children = [...targetStep.children];
            }
            
            if (isSameDocument && !moveCreateCopy && isSameStep) {
              // Remove from original position (only for same-step moves, not copies)
              // Find the actual child node in targetStep.children to ensure we clone the correct one
              const sourceChildIndex = targetStep.children.findIndex(
                child => {
                  const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
                  const matches = childId === moveNode.nodeId || child.tree_node_id === moveNode.nodeId;
                  return matches;
                }
              );
              
              if (sourceChildIndex !== -1) {
                // Get the actual child node at this index
                const actualSourceChild = targetStep.children[sourceChildIndex];
                
                // Clone the actual child from targetStep to ensure all properties match
                const childToMove = structuredClone(actualSourceChild);
                
                // Remove from original position
                targetStep.children.splice(sourceChildIndex, 1);
                
                // Find the maximum sibling_order in remaining children to place moved child at the end
                const maxSiblingOrder = targetStep.children.reduce((max, child) => {
                  const order = parseInt(child.sibling_order || child.procedure_tree?.sibling_order || '0', 10);
                  return Math.max(max, order);
                }, 0);
                
                // Set sibling_order to be last (max + 1) so it appears at the end when sorted
                childToMove.sibling_order = (maxSiblingOrder + 1).toString();
                if (childToMove.procedure_tree) {
                  childToMove.procedure_tree.sibling_order = (maxSiblingOrder + 1).toString();
                }
                
                // Add cloned node to end (after removal)
                targetStep.children.push(childToMove);
              } else {
                // Source not found in target, just add the cloned node (shouldn't happen for same-step moves)
                targetStep.children.push(clonedChildNode);
              }
            } else if (isSameDocument && !moveCreateCopy && !isSameStep) {
              // Cross-step move: remove from source parent
              let sourceParentInTarget = null;
              if (sourceParent.substep_id_ref) {
                // Source is in substep
                for (const section of sortedSections) {
                  for (const step of section.steps || []) {
                    sourceParentInTarget = step.substeps?.find(
                      substep => substep.substep_id_ref === sourceParent.substep_id_ref ||
                                substep.tree_node_id === sourceParent.tree_node_id
                    );
                    if (sourceParentInTarget) break;
                  }
                  if (sourceParentInTarget) break;
                }
              } else {
                // Source is in step
                sourceParentInTarget = sortedSections
                  .flatMap(s => s.steps || [])
                  .find(step => step.step_id_ref === sourceParent.step_id_ref);
              }
              
              if (sourceParentInTarget && sourceParentInTarget.children) {
                // Ensure source parent's children array is mutable
                sourceParentInTarget.children = [...sourceParentInTarget.children];
                const sourceChildIndex = sourceParentInTarget.children.findIndex(
                  child => {
                    const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
                    return childId === moveNode.nodeId || child.tree_node_id === moveNode.nodeId;
                  }
                );
                if (sourceChildIndex !== -1) {
                  sourceParentInTarget.children.splice(sourceChildIndex, 1);
                }
              }
              // Add cloned node to target step
              targetStep.children.push(clonedChildNode);
              targetStep.children.forEach((child, index) => {
                child.sibling_order = index.toString();
                if (child.procedure_tree) {
                  child.procedure_tree.sibling_order = index.toString();
                }
              });
            } else {
              // Copy operation or cross-document move: just add (don't remove source)
              targetStep.children.push(clonedChildNode);
              
              // Update sibling_order for all children to reflect their positions
              // This is critical for the preview to display nodes in the correct order
              targetStep.children.forEach((child, index) => {
                child.sibling_order = index.toString();
                if (child.procedure_tree) {
                  child.procedure_tree.sibling_order = index.toString();
                }
              });
            }
            
            newTargetData = sortedSections;
          } else if (targetType === 'substep') {
            // Target is a substep
            // Find the section and step containing the target substep
            let targetSection = null;
            let targetStep = null;
            let targetSubstep = null;
            
            for (const section of sortedSections) {
              for (const step of section.steps || []) {
                targetSubstep = step.substeps?.find(
                  substep => substep.tree_node_id === targetId || 
                             substep.substep_id_ref === targetId
                );
                if (targetSubstep) {
                  targetStep = step;
                  targetSection = section;
                  break;
                }
              }
              if (targetSubstep) break;
            }
            
            if (!targetSubstep || !targetStep || !targetSection) {
              dispatch(showSnackbar({ message: 'Target substep not found', type: 'error' }));
              return;
            }

            // Ensure section's steps array is mutable
            if (!targetSection.steps) {
              targetSection.steps = [];
            } else {
              targetSection.steps = [...targetSection.steps];
            }
            
            // Find the step index in the section's steps array and update the reference
            const targetStepIndex = targetSection.steps.findIndex(
              step => step.tree_node_id === targetStep.tree_node_id || step.step_id_ref === targetStep.step_id_ref
            );
            if (targetStepIndex !== -1) {
              targetStep = targetSection.steps[targetStepIndex];
              
              // Ensure step's substeps array is mutable
              if (!targetStep.substeps) {
                targetStep.substeps = [];
              } else {
                targetStep.substeps = [...targetStep.substeps];
              }
              
              // Find the substep index in the step's substeps array and update the reference
              const targetSubstepIndex = targetStep.substeps.findIndex(
                substep => substep.tree_node_id === targetSubstep.tree_node_id || substep.substep_id_ref === targetSubstep.substep_id_ref
              );
              if (targetSubstepIndex !== -1) {
                targetSubstep = targetStep.substeps[targetSubstepIndex];
              }
            }

            // For same-document MOVES (not copies), remove the source child node from its original position
            const isSameDocument = moveDocument.partId === targetDoc.partId && moveDocument.revisionId === targetDoc.revisionId;
            const isSameSubstep = sourceParent.substep_id_ref && sourceParent.substep_id_ref === targetSubstep.substep_id_ref;
            
            // Ensure target substep's children array is mutable (do this once, before any operations)
            if (!targetSubstep.children) {
              targetSubstep.children = [];
            } else {
              targetSubstep.children = [...targetSubstep.children];
            }
            
            if (isSameDocument && !moveCreateCopy && isSameSubstep) {
              // Remove from original position (only for same-substep moves, not copies)
              // Find the actual child node in targetSubstep.children to ensure we clone the correct one
              const sourceChildIndex = targetSubstep.children.findIndex(
                child => {
                  const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
                  const matches = childId === moveNode.nodeId || child.tree_node_id === moveNode.nodeId;
                  return matches;
                }
              );
              
              if (sourceChildIndex !== -1) {
                // Get the actual child node at this index
                const actualSourceChild = targetSubstep.children[sourceChildIndex];
                
                // Clone the actual child from targetSubstep to ensure all properties match
                const childToMove = structuredClone(actualSourceChild);
                
                // Remove from original position
                targetSubstep.children.splice(sourceChildIndex, 1);
                
                // Find the maximum sibling_order in remaining children to place moved child at the end
                const maxSiblingOrder = targetSubstep.children.reduce((max, child) => {
                  const order = parseInt(child.sibling_order || child.procedure_tree?.sibling_order || '0', 10);
                  return Math.max(max, order);
                }, 0);
                
                // Set sibling_order to be last (max + 1) so it appears at the end when sorted
                childToMove.sibling_order = (maxSiblingOrder + 1).toString();
                if (childToMove.procedure_tree) {
                  childToMove.procedure_tree.sibling_order = (maxSiblingOrder + 1).toString();
                }
                
                // Add cloned node to end (after removal)
                targetSubstep.children.push(childToMove);
              } else {
                // Source not found in target, just add the cloned node (shouldn't happen for same-substep moves)
                targetSubstep.children.push(clonedChildNode);
              }
            } else if (isSameDocument && !moveCreateCopy && !isSameSubstep) {
              // Cross-substep move: remove from source parent
              let sourceParentInTarget = null;
              if (sourceParent.substep_id_ref) {
                // Source is in substep
                for (const section of sortedSections) {
                  for (const step of section.steps || []) {
                    sourceParentInTarget = step.substeps?.find(
                      substep => substep.substep_id_ref === sourceParent.substep_id_ref ||
                                substep.tree_node_id === sourceParent.tree_node_id
                    );
                    if (sourceParentInTarget) break;
                  }
                  if (sourceParentInTarget) break;
                }
              } else {
                // Source is in step
                sourceParentInTarget = sortedSections
                  .flatMap(s => s.steps || [])
                  .find(step => step.step_id_ref === sourceParent.step_id_ref);
              }
              
              if (sourceParentInTarget && sourceParentInTarget.children) {
                // Ensure source parent's children array is mutable
                sourceParentInTarget.children = [...sourceParentInTarget.children];
                const sourceChildIndex = sourceParentInTarget.children.findIndex(
                  child => {
                    const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
                    return childId === moveNode.nodeId || child.tree_node_id === moveNode.nodeId;
                  }
                );
                if (sourceChildIndex !== -1) {
                  sourceParentInTarget.children.splice(sourceChildIndex, 1);
                }
              }
              // Add cloned node to target substep
              targetSubstep.children.push(clonedChildNode);
              // Update sibling_order for all children to reflect their positions
              // This is critical for the preview to display nodes in the correct order
              targetSubstep.children.forEach((child, index) => {
                child.sibling_order = index.toString();
                if (child.procedure_tree) {
                  child.procedure_tree.sibling_order = index.toString();
                }
              });
            } else {
              // Copy operation or cross-document move: just add (don't remove source)
              targetSubstep.children.push(clonedChildNode);
              
              // Update sibling_order for all children to reflect their positions
              // This is critical for the preview to display nodes in the correct order
              targetSubstep.children.forEach((child, index) => {
                child.sibling_order = index.toString();
                if (child.procedure_tree) {
                  child.procedure_tree.sibling_order = index.toString();
                }
              });
            }
            
            newTargetData = sortedSections;
          }
        } else if (position === 'above' || position === 'below') {
          // Move/copy child node ABOVE/BELOW another child node
          let targetChildNode = null;
          let targetParent = null; // Step or substep
          let targetSection = null;
          
          // Helper function to check if a child node matches the targetId
          // Must check all possible ID fields since target could be any child node type
          const matchesTargetId = (child) => {
            // Check tree_node_id first (most common)
            if (child.tree_node_id === targetId) return true;
            
            // Check all possible detail ID fields for different child node types
            const detailIdFields = [
              'canvas_tree_node_id',
              'attachment_tree_node_id',
              'video_tree_node_id',
              'rich_text_node_id',
              'form_tree_node_id',
            ];
            
            for (const field of detailIdFields) {
              if (child.detail?.[field] === targetId) return true;
            }
            
            // Also check if nodeIdKey matches (for the source node type)
            if (child.detail?.[nodeIdKey] === targetId) return true;
            
            return false;
          };
          
          // Find target child node
          for (const section of sortedSections) {
            for (const step of section.steps || []) {
              // Check step-level children
              targetChildNode = step.children?.find(matchesTargetId);
              if (targetChildNode) {
                targetParent = step;
                targetSection = section;
                break;
              }

              // Check substep-level children
              for (const substep of step.substeps || []) {
                targetChildNode = substep.children?.find(matchesTargetId);
                if (targetChildNode) {
                  targetParent = substep;
                  targetSection = section;
                  break;
                }
              }
              if (targetChildNode) break;
            }
            if (targetChildNode) break;
          }
          
          if (!targetChildNode || !targetParent) {
            dispatch(showSnackbar({ message: 'Target child node not found', type: 'error' }));
            return;
          }

          // Ensure the entire parent structure is mutable (section -> step -> substep -> children)
          // This is critical for copy operations to work correctly
          // We need to update references in the sortedSections structure to point to mutable copies
          
          // First, ensure section's steps array is mutable
          if (targetSection && targetSection.steps) {
            targetSection.steps = [...targetSection.steps];
          }
          
          // If targetParent is a substep, we need to find its parent step and make substeps mutable
          if (targetParent.substep_id_ref) {
            // Find the step that contains this substep
            const parentStep = targetSection?.steps?.find(step => 
              step.substeps?.some(substep => 
                substep.substep_id_ref === targetParent.substep_id_ref || 
                substep.tree_node_id === targetParent.tree_node_id
              )
            );
            
            if (parentStep) {
              // Make parent step's substeps array mutable
              if (!parentStep.substeps) {
                parentStep.substeps = [];
              } else {
                parentStep.substeps = [...parentStep.substeps];
              }
              
              // Find and update targetParent reference to point to the mutable substep
              const substepIndex = parentStep.substeps.findIndex(
                ss => ss.substep_id_ref === targetParent.substep_id_ref || 
                      ss.tree_node_id === targetParent.tree_node_id
              );
              if (substepIndex !== -1) {
                targetParent = parentStep.substeps[substepIndex];
              }
            }
          } else if (targetSection && targetSection.steps) {
            // targetParent is a step, find it in the mutable steps array
            const stepIndex = targetSection.steps.findIndex(
              s => s.step_id_ref === targetParent.step_id_ref || 
                   s.tree_node_id === targetParent.tree_node_id ||
                   s === targetParent  // Direct reference match
            );
            if (stepIndex !== -1) {
              targetParent = targetSection.steps[stepIndex];
            }
          }

          // Ensure target parent's children array is mutable
          if (!targetParent.children) {
            targetParent.children = [];
          } else {
            targetParent.children = [...targetParent.children];
          }

          // Helper function to check if a child matches targetId (same as above)
          const matchesTargetIdForIndex = (child) => {
            if (child.tree_node_id === targetId) return true;
            const detailIdFields = [
              'canvas_tree_node_id',
              'attachment_tree_node_id',
              'video_tree_node_id',
              'rich_text_node_id',
              'form_tree_node_id',
            ];
            for (const field of detailIdFields) {
              if (child.detail?.[field] === targetId) return true;
            }
            if (child.detail?.[nodeIdKey] === targetId) return true;
            return false;
          };
          
          const targetIndex = targetParent.children.findIndex(matchesTargetIdForIndex);

          if (targetIndex !== -1) {
            const isSameDocument = moveDocument.partId === targetDoc.partId && moveDocument.revisionId === targetDoc.revisionId;
            const sourceParentIdRef = sourceParent.substep_id_ref || sourceParent.sub_step_tree_node_id || sourceParent.step_id_ref;
            const targetParentIdRef = targetParent.substep_id_ref || targetParent.sub_step_tree_node_id || targetParent.step_id_ref;
            const isSameContainer = sourceParentIdRef === targetParentIdRef;

            if (moveCreateCopy) {
              // Copy: just insert, don't remove
              if (position === 'above') {
                targetParent.children.splice(targetIndex, 0, clonedChildNode);
              } else {
                targetParent.children.splice(targetIndex + 1, 0, clonedChildNode);
              }
              
              // Update sibling_order for all children to reflect their new positions
              // This is critical for the preview to display nodes in the correct order
              targetParent.children.forEach((child, index) => {
                child.sibling_order = index.toString();
                // Also update procedure_tree.sibling_order if it exists
                if (child.procedure_tree) {
                  child.procedure_tree.sibling_order = index.toString();
                }
              });
            } else {
              // Move: remove from source, insert at target
              if (isSameDocument && isSameContainer) {
                // Same-container move: remove from source position first
                // Helper to match source node (the one being moved)
                const matchesSourceNodeId = (child) => {
                  if (child.tree_node_id === moveNode.nodeId) return true;
                  // Check the specific nodeIdKey for the source node type
                  if (child.detail?.[nodeIdKey] === moveNode.nodeId) return true;
                  // Also check all possible detail ID fields for safety
                  const detailIdFields = [
                    'canvas_tree_node_id',
                    'attachment_tree_node_id',
                    'video_tree_node_id',
                    'rich_text_node_id',
                    'form_tree_node_id',
                  ];
                  for (const field of detailIdFields) {
                    if (child.detail?.[field] === moveNode.nodeId) return true;
                  }
                  return false;
                };
                const sourceIndex = targetParent.children.findIndex(matchesSourceNodeId);
                
                if (sourceIndex !== -1) {
                  targetParent.children.splice(sourceIndex, 1);
                  
                  // Adjust target index if needed
                  let adjustedTargetIndex = targetIndex;
                  if (sourceIndex < targetIndex) {
                    adjustedTargetIndex = targetIndex - 1;
                  }
                  
                  // Insert at adjusted position
                  if (position === 'above') {
                    targetParent.children.splice(adjustedTargetIndex, 0, clonedChildNode);
                  } else {
                    targetParent.children.splice(adjustedTargetIndex + 1, 0, clonedChildNode);
                  }
                } else {
                  // Source not found, just insert
                  if (position === 'above') {
                    targetParent.children.splice(targetIndex, 0, clonedChildNode);
                  } else {
                    targetParent.children.splice(targetIndex + 1, 0, clonedChildNode);
                  }
                }
                // Update sibling_order for all children to reflect their new positions
                // This is critical for the preview to display nodes in the correct order
                targetParent.children.forEach((child, index) => {
                  child.sibling_order = index.toString();
                  // Also update procedure_tree.sibling_order if it exists
                  if (child.procedure_tree) {
                    child.procedure_tree.sibling_order = index.toString();
                  }
                });
              } else {
                // Cross-container or cross-document move: remove from source, insert at target
                let sourceParentInTarget = null;
                if (sourceParent.substep_id_ref) {
                  // Source is in substep
                  for (const section of sortedSections) {
                    for (const step of section.steps || []) {
                      sourceParentInTarget = step.substeps?.find(
                        substep => substep.substep_id_ref === sourceParent.substep_id_ref ||
                                  substep.tree_node_id === sourceParent.tree_node_id
                      );
                      if (sourceParentInTarget) break;
                    }
                    if (sourceParentInTarget) break;
                  }
                } else {
                  // Source is in step
                  sourceParentInTarget = sortedSections
                    .flatMap(s => s.steps || [])
                    .find(step => step.step_id_ref === sourceParent.step_id_ref);
                }
                
                if (sourceParentInTarget && sourceParentInTarget.children) {
                  // Ensure source parent's children array is mutable
                  sourceParentInTarget.children = [...sourceParentInTarget.children];
                  // Helper to match source node (the one being moved)
                  const matchesSourceNodeId = (child) => {
                    if (child.tree_node_id === moveNode.nodeId) return true;
                    if (child.detail?.[nodeIdKey] === moveNode.nodeId) return true;
                    const detailIdFields = [
                      'canvas_tree_node_id',
                      'attachment_tree_node_id',
                      'video_tree_node_id',
                      'rich_text_node_id',
                      'form_tree_node_id',
                    ];
                    for (const field of detailIdFields) {
                      if (child.detail?.[field] === moveNode.nodeId) return true;
                    }
                    return false;
                  };
                  const sourceChildIndex = sourceParentInTarget.children.findIndex(matchesSourceNodeId);
                  if (sourceChildIndex !== -1) {
                    sourceParentInTarget.children.splice(sourceChildIndex, 1);
                  }
                }
                
                // Insert at target position
                if (position === 'above') {
                  targetParent.children.splice(targetIndex, 0, clonedChildNode);
                } else {
                  targetParent.children.splice(targetIndex + 1, 0, clonedChildNode);
                }
                // Update sibling_order for all children to reflect their new positions
                // This is critical for the preview to display nodes in the correct order
                targetParent.children.forEach((child, index) => {
                  child.sibling_order = index.toString();
                  // Also update procedure_tree.sibling_order if it exists
                  if (child.procedure_tree) {
                    child.procedure_tree.sibling_order = index.toString();
                  }
                });
              }
            }
          }
          
          newTargetData = sortedSections;
        }
      }
      
      // At this point, sortedSections contains all our modifications
      // Deep clone it to create the final immutable copy for Redux
      const finalNewData = structuredClone(sortedSections);
      
      // Dispatch the computed new data
      dispatch(setMoveNewData(finalNewData));
      
      // Move to preview step
      setStep('preview');
    } catch (error) {
      console.error('Error computing new structure:', error);
      dispatch(showSnackbar({ message: 'Error computing preview: ' + (error?.message || 'Unknown error'), type: 'error' }));
    } finally {
      dispatch(setMoveLoading(false));
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (step === 'preview') {
      // Navigate to view nodes screen instead of going back to position selector
      dispatch(closeMove());
    } else if (step === 'position' && isCrossDocumentMove) {
      setStep('select');
    }
  };

  // Handle close
  const handleClose = () => {
    dispatch(closeMove());
  };

  // Handle move confirmation
  const handleConfirmMove = async () => {
    if (!moveComment || !moveComment.trim()) {
      dispatch(showSnackbar({ message: 'Comment is mandatory.', type: 'error' }));
      return;
    }

    if (!moveDocument || !moveNode) {
      dispatch(showSnackbar({ message: 'Move node information is missing.', type: 'error' }));
      return;
    }

    
    dispatch(setMoveLoading(true));

    try {
      // Fetch source document data
      const sourceAllNodes = await dispatch(getRevAllNodes({ 
        partId: moveDocument.partId, 
        revisionId: moveDocument.revisionId 
      })).unwrap();

      // Get target document
      const targetDoc = moveTargetDocument || moveDocument;
      const targetAllNodes = moveOriginalData || await dispatch(getRevAllNodes({ 
        partId: targetDoc.partId, 
        revisionId: targetDoc.revisionId 
      })).unwrap();

      // Handle step or section based on node type
      if (moveNode.nodeType === 'step') {
        // Find source step
        let sourceStep = null;
        let sourceSection = null;
        
        for (const section of sourceAllNodes) {
          sourceStep = section.steps?.find(
            step => step.tree_node_id === moveNode.nodeId || 
                    step.step_id_ref === moveNode.nodeId
          );
          if (sourceStep) {
            sourceSection = section;
            break;
          }
        }

        if (!sourceStep || !sourceSection) {
          dispatch(showSnackbar({ message: 'Source step not found.', type: 'error' }));
          dispatch(setMoveLoading(false));
          return;
        }

        // Calculate step move parameters using utility function
        let moveParams;
        try {
          moveParams = calculateStepMoveParameters({
            position: movePosition.position,
            targetAllNodes,
            targetDoc,
            movePosition,
            moveCreateCopy,
            sourceStep, // Pass source step for order adjustment calculation
            moveDocument, // Pass move document to check if same-document move
          });
        } catch (error) {
          dispatch(showSnackbar({ message: error.message || 'Target step/section not found.', type: 'error' }));
          dispatch(setMoveLoading(false));
          return;
        }

        const { toOrder, toProcedureTreeNodeRef, toSectionIdRef, toStepIdRef, direction, direction_move_into } = moveParams;

        // Prepare new content structure for version history
        const newContent = prepareStepMoveNewContent({
          targetAllNodes,
          sourceStep,
          moveDocument,
          targetDoc,
          position: movePosition.position,
          movePosition,
          toSectionIdRef,
          moveCreateCopy,
        });

        if (moveCreateCopy) {
          // Build step copy payload using utility function
          const copyPayload = await buildStepCopyPayload({
            sourceStep,
            moveDocument,
            targetDoc,
            position: movePosition.position,
            movePosition,
            toOrder,
            toProcedureTreeNodeRef,
            toSectionIdRef,
            toStepIdRef,
            direction,
            direction_move_into,
            moveComment,
            targetAllNodesOriginal: targetAllNodes,
            newContent,
            sourceAllNodes,
          });

          const revAndTrainingData = await getRevAndTraining(dispatch, copyPayload.from_part_id_ref)

          const snapshot_url = await uploadJsonToS3({
            ...copyPayload.node_indexing,
            version_content:{
              action_to_show:copyPayload.audit_trail_data.version_content.action_to_show,
              item_code_type:targetDoc.type,
              partNumber:targetDoc.partNumber,
              status:targetDoc.status,
              revision:targetDoc.revision,
              partDescription: targetDoc.description,
              operation:"bulk_audit_trail",
              ...revAndTrainingData,
            }
          },targetDoc.partNumber,targetDoc.revision+"STEP_COPIED",true)

          const current_snapshot = await uploadJsonToS3(
            prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }),
            targetDoc.partNumber,targetDoc.revision)

          const current_updated_snapshot = await uploadJsonToS3(
            prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }),
            targetDoc.partNumber,targetDoc.revision+"_current_updated_snapshot",true)

          Object.entries(copyPayload.node_indexing).forEach(([key, value]) => {
            copyPayload.node_indexing[key]={
              title:value.title,
              previous_content_snapshot:snapshot_url,
              new_content_snapshot:snapshot_url,
            }
          });
          
          copyPayload.current_snapshot = current_snapshot;
          copyPayload.current_updated_snapshot = current_updated_snapshot;
          copyPayload.audit_trail_data.previous_content_snapshot = snapshot_url
          copyPayload.audit_trail_data.new_content_snapshot = snapshot_url

          copyPayload.audit_trail_data.version_content = {
            ...copyPayload.audit_trail_data.version_content,
            main_entry: true,
            preview_tree_node_id: copyPayload.from_procedure_tree_node_ref,
          }

          // Make API call to copy step
          const response = await dispatch(copyElement(copyPayload)).unwrap();
          
          dispatch(showSnackbar({ message: 'Step copied successfully!', type: 'success' }));
          
          
          
          // Redirect to copied node if copy was successful
          const redirected = await handleRedirectToCopiedNode(
            response,
            targetDoc,
            dispatch,
            navigate,
            getRevAllNodes,
            setMoveLoading
          );          
          setModalOpen(false);
          handleClose();
         
        } else {
          // Build step move payload using utility function
          const movePayload = await buildStepMovePayload({
            sourceStep,
            moveDocument,
            targetDoc,
            position: movePosition.position,
            movePosition,
            moveNode,
            toOrder,
            toProcedureTreeNodeRef,
            toSectionIdRef,
            toStepIdRef,
            direction,
            direction_move_into,
            moveComment,
            targetAllNodesOriginal: targetAllNodes,
            newContent,
            sourceAllNodes,
          });

          const revAndTrainingData = await getRevAndTraining(dispatch, movePayload.from_part_id_ref)

          const snapshot_url = await uploadJsonToS3({
            ...movePayload.node_indexing,
            version_content:{
              action_to_show:movePayload.audit_trail_data.version_content.action_to_show,
              item_code_type:targetDoc.type,
              partNumber:targetDoc.partNumber,
              status:targetDoc.status,
              revision:targetDoc.revision,
              partDescription: targetDoc.description,
              operation:"bulk_audit_trail",
              ...revAndTrainingData,
            }
          },targetDoc.partNumber,targetDoc.revision+"STEP_MOVED",true)

          const current_snapshot = await uploadJsonToS3(
            prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }),
            targetDoc.partNumber,targetDoc.revision)

          const current_updated_snapshot = await uploadJsonToS3(
            prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }),
            targetDoc.partNumber,targetDoc.revision+"_current_updated_snapshot",true)

          Object.entries(movePayload.node_indexing).forEach(([key, value]) => {
            movePayload.node_indexing[key]={
              title:value.title,
              previous_content_snapshot:snapshot_url,
              new_content_snapshot:snapshot_url,
            }
          });
          movePayload.current_snapshot = current_snapshot;
          movePayload.current_updated_snapshot = current_updated_snapshot
          movePayload.audit_trail_data.previous_content_snapshot = snapshot_url
          movePayload.audit_trail_data.new_content_snapshot = snapshot_url

          movePayload.audit_trail_data.version_content = {
            action_to_show:movePayload.audit_trail_data.version_content.action_to_show,
            main_entry:true,
            preview_tree_node_id:movePayload.from_procedure_tree_node_ref
          }
          // Make API call to move step
          await dispatch(moveStep(movePayload)).unwrap();
          
          dispatch(showSnackbar({ message: 'Step moved successfully!', type: 'success' }));
          
          setModalOpen(false);
          handleClose();
          
          // Navigate to target document's revision page
          const docType = targetDoc.type || 'Document';
          navigate(
            `/revision-submission/${targetDoc.partId}/${targetDoc.revision}/${docType}/${targetDoc.partNumber}/${targetDoc.revisionId}/no-description`);
        }

        return; // Exit early for step handling
      }

      // Handle substep move/copy
      if (moveNode.nodeType === 'substep') {
        // Find source substep
        let sourceSubstep = null;
        let sourceStep = null;
        let sourceSection = null;
        
        for (const section of sourceAllNodes) {
          for (const step of section.steps || []) {
            sourceSubstep = step.substeps?.find(
              substep => substep.tree_node_id === moveNode.nodeId || 
                         substep.substep_id_ref === moveNode.nodeId
            );
            if (sourceSubstep) {
              sourceStep = step;
              sourceSection = section;
              break;
            }
          }
          if (sourceSubstep) break;
        }

        if (!sourceSubstep || !sourceStep || !sourceSection) {
          dispatch(showSnackbar({ message: 'Source substep not found.', type: 'error' }));
          dispatch(setMoveLoading(false));
          return;
        }

        // Calculate substep move parameters using utility function
        let moveParams;
        try {
          moveParams = calculateSubstepMoveParameters({
            position: movePosition.position,
            targetAllNodes,
            targetDoc,
            movePosition,
            moveCreateCopy,
            sourceSubstep, // Pass source substep for order adjustment calculation
            moveDocument, // Pass move document to check if same-document move
          });
        } catch (error) {
          dispatch(showSnackbar({ message: error.message || 'Target substep/step not found.', type: 'error' }));
          dispatch(setMoveLoading(false));
          return;
        }

        const { toOrder, toProcedureTreeNodeRef, toSectionIdRef, toStepIdRef, toSubstepIdRef, direction, direction_move_into } = moveParams;

        // Prepare new content structure for version history
        const newContent = prepareSubstepMoveNewContent({
          targetAllNodes,
          sourceSubstep,
          moveDocument,
          targetDoc,
          position: movePosition.position,
          movePosition,
          toStepIdRef,
          moveCreateCopy,
        });

        if (moveCreateCopy) {
          // Build substep copy payload using utility function
          const copyPayload = await buildSubstepCopyPayload({
            sourceSubstep,
            moveDocument,
            targetDoc,
            position: movePosition.position,
            movePosition,
            toOrder,
            toProcedureTreeNodeRef,
            toSectionIdRef,
            toStepIdRef,
            toSubstepIdRef,
            direction,
            direction_move_into,
            moveComment,
            targetAllNodesOriginal: targetAllNodes,
            sourceAllNodes,
            newContent,
            item_code_type: targetDoc.type,
          });

          const revAndTrainingData = await getRevAndTraining(dispatch, copyPayload.from_part_id_ref)

          const snapshot_url = await uploadJsonToS3({
            ...copyPayload.node_indexing,
            version_content:{
              action_to_show:copyPayload.audit_trail_data.version_content.action_to_show,
              item_code_type:targetDoc.type,
              partNumber:targetDoc.partNumber,
              status:targetDoc.status,
              revision:targetDoc.revision,
              partDescription: targetDoc.description,
              operation:"bulk_audit_trail",
              ...revAndTrainingData,
            }
          },targetDoc.partNumber,targetDoc.revision+"SUBSTEP_COPIED",true)

          const current_snapshot = await uploadJsonToS3(
            prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }),
            targetDoc.partNumber,targetDoc.revision)

          const current_updated_snapshot = await uploadJsonToS3(
            prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }),
            targetDoc.partNumber,targetDoc.revision+"_current_updated_snapshot",true)

          Object.entries(copyPayload.node_indexing).forEach(([key, value]) => {
            copyPayload.node_indexing[key]={
              title:value.title,
              previous_content_snapshot:snapshot_url,
              new_content_snapshot:snapshot_url,
            }
          });

          copyPayload.current_snapshot = current_snapshot
          copyPayload.current_updated_snapshot=current_updated_snapshot;
          copyPayload.audit_trail_data.previous_content_snapshot = snapshot_url
          copyPayload.audit_trail_data.new_content_snapshot = snapshot_url

          copyPayload.audit_trail_data.version_content = {
            ...copyPayload.audit_trail_data.version_content,
            main_entry: true,
            preview_tree_node_id: copyPayload.from_procedure_tree_node_ref,
          }

          // Make API call to copy substep
          const response = await dispatch(copyElement(copyPayload)).unwrap();
          
          dispatch(showSnackbar({ message: 'Substep copied successfully!', type: 'success' }));
          
          
          
          // Redirect to copied node if copy was successful
          const redirected = await handleRedirectToCopiedNode(
            response,
            targetDoc,
            dispatch,
            navigate,
            getRevAllNodes,
            setMoveLoading
          );

          setModalOpen(false);
          handleClose();
        } else {
          // Build substep move payload using utility function
          const movePayload = await buildSubstepMovePayload({
            sourceSubstep,
            moveDocument,
            targetDoc,
            position: movePosition.position,
            movePosition,
            moveNode,
            toOrder,
            toProcedureTreeNodeRef,
            toSectionIdRef,
            toStepIdRef,
            toSubstepIdRef,
            direction,
            direction_move_into,
            moveComment,
            targetAllNodesOriginal: targetAllNodes,
            sourceAllNodes,
            newContent,
          });

          const revAndTrainingData = await getRevAndTraining(dispatch, movePayload.from_part_id_ref)

          const snapshot_url = await uploadJsonToS3({
            ...movePayload.node_indexing,
            version_content:{
              action_to_show:movePayload.audit_trail_data.version_content.action_to_show,
              item_code_type:targetDoc.type,
              partNumber:targetDoc.partNumber,
              status:targetDoc.status,
              revision:targetDoc.revision,
              partDescription: targetDoc.description,
              operation:"bulk_audit_trail",
              ...revAndTrainingData,
            }
          },targetDoc.partNumber,targetDoc.revision+"SUBSTEP_MOVED",true)

          const current_snapshot = await uploadJsonToS3(
            prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }),
            targetDoc.partNumber,targetDoc.revision)

          const current_updated_snapshot = await uploadJsonToS3(
            prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }),
            targetDoc.partNumber,targetDoc.revision+"_current_updated_snapshot",true)

          Object.entries(movePayload.node_indexing).forEach(([key, value]) => {
            movePayload.node_indexing[key]={
              title:value.title,
              previous_content_snapshot:snapshot_url,
              new_content_snapshot:snapshot_url,
            }
          });
 
          movePayload.current_snapshot = current_snapshot;
          movePayload.current_updated_snapshot = current_updated_snapshot;
          movePayload.audit_trail_data.previous_content_snapshot = snapshot_url
          movePayload.audit_trail_data.new_content_snapshot = snapshot_url

          movePayload.audit_trail_data.version_content = {
            action_to_show:movePayload.audit_trail_data.version_content.action_to_show,
            main_entry:true,
            preview_tree_node_id:movePayload.from_procedure_tree_node_ref
          }
          // Make API call to move substep
          await dispatch(moveSubstep(movePayload)).unwrap();
          
          dispatch(showSnackbar({ message: 'Substep moved successfully!', type: 'success' }));
          
          setModalOpen(false);
          handleClose();
          
          // Navigate to target document's revision page
          const docType = targetDoc.type || 'Document';
          navigate(
            `/revision-submission/${targetDoc.partId}/${targetDoc.revision}/${docType}/${targetDoc.partNumber}/${targetDoc.revisionId}/no-description`);
        }

        return; // Exit early for substep handling
      }

      // Handle child node move/copy (attachment, video, rich-text, canvas, form)
      if (['attachment', 'video', 'rich-text', 'form', 'canva', 'canvas'].includes(moveNode.nodeType)) {
        const childNodeType = moveNode.nodeType === 'canva' ? 'canvas' : moveNode.nodeType;
        
        // Get the node ID key based on child node type
        const getNodeIdKey = (type) => {
          const keyMap = {
            'attachment': 'attachment_tree_node_id',
            'video': 'video_tree_node_id',
            'rich-text': 'rich_text_node_id',
            'canvas': 'canvas_tree_node_id',
            'form': 'form_tree_node_id',
          };
          return keyMap[type] || 'tree_node_id';
        };

        const nodeIdKey = getNodeIdKey(childNodeType);
        
        // Find source child node
        let sourceChildNode = null;
        let sourceParent = null; // Step or substep
        let sourceSection = null;
        
        // Search through all sections, steps, and substeps
        for (const section of sourceAllNodes) {
          for (const step of section.steps || []) {
            // Check step-level children
            sourceChildNode = step.children?.find(child => {
              const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
              return childId === moveNode.nodeId || child.tree_node_id === moveNode.nodeId;
            });
            if (sourceChildNode) {
              sourceParent = step;
              sourceSection = section;
              break;
            }

            // Check substep-level children
            for (const substep of step.substeps || []) {
              sourceChildNode = substep.children?.find(child => {
                const childId = child.detail?.[nodeIdKey] || child.tree_node_id;
                return childId === moveNode.nodeId || child.tree_node_id === moveNode.nodeId;
              });
              if (sourceChildNode) {
                sourceParent = substep;
                sourceSection = section;
                break;
              }
            }
            if (sourceChildNode) break;
          }
          if (sourceChildNode) break;
        }

        if (!sourceChildNode || !sourceParent || !sourceSection) {
          dispatch(showSnackbar({ message: 'Source child node not found.', type: 'error' }));
          dispatch(setMoveLoading(false));
          return;
        }

        // Calculate child node move parameters using utility function
        let moveParams;
        try {
          moveParams = calculateChildNodeMoveParameters({
            position: movePosition.position,
            targetAllNodes,
            targetDoc,
            movePosition,
            moveCreateCopy,
            sourceChildNode,
            moveDocument,
            childNodeType,
          });
        } catch (error) {
          dispatch(showSnackbar({ message: error.message || 'Target child node/container not found.', type: 'error' }));
          dispatch(setMoveLoading(false));
          return;
        }

        const { toOrder, toProcedureTreeNodeRef, toSectionIdRef, toStepIdRef, toSubstepIdRef, toChildNodeRef, direction, direction_move_into } = moveParams;

        // Prepare new content structure for version history
        const newContent = prepareChildNodeMoveNewContent({
          targetAllNodes,
          sourceChildNode,
          moveDocument,
          targetDoc,
          position: movePosition.position,
          movePosition,
          childNodeType,
          moveCreateCopy,
        });

        if (moveCreateCopy) {
          // Build child node copy payload using utility function
          const copyPayload = await buildChildNodeCopyPayload({
            sourceChildNode,
            moveDocument,
            targetDoc,
            position: movePosition.position,
            movePosition,
            toOrder,
            toProcedureTreeNodeRef,
            toSectionIdRef,
            toStepIdRef,
            toSubstepIdRef,
            toChildNodeRef,
            direction,
            direction_move_into,
            moveComment,
            targetAllNodesOriginal: targetAllNodes,
            newContent,
            childNodeType,
            dispatch
          });

          // Make API call to copy child node
          const response = await dispatch(copyElement(copyPayload)).unwrap();
          
          dispatch(showSnackbar({ message: `${childNodeType.charAt(0).toUpperCase() + childNodeType.slice(1)} copied successfully!`, type: 'success' }));
          
          
          
          // Redirect to copied node if copy was successful
          const redirected = await handleRedirectToCopiedNode(
            response,
            targetDoc,
            dispatch,
            navigate,
            getRevAllNodes,
            setMoveLoading
          );

          setModalOpen(false);
          handleClose();
        } else {
          // Build child node move payload using utility function
          const movePayload = await buildChildNodeMovePayload({
            sourceChildNode,
            moveDocument,
            targetDoc,
            position: movePosition.position,
            movePosition,
            moveNode,
            toOrder,
            toProcedureTreeNodeRef,
            toSectionIdRef,
            toStepIdRef,
            toSubstepIdRef,
            toChildNodeRef,
            direction,
            direction_move_into,
            moveComment,
            targetAllNodesOriginal: targetAllNodes,
            newContent,
            childNodeType,
            dispatch
          });

          // Make API call to move child node - use moveChildnodes action which calls the move endpoint
          await dispatch(moveChildnodes(movePayload)).unwrap();
          
          dispatch(showSnackbar({ message: `${childNodeType.charAt(0).toUpperCase() + childNodeType.slice(1)} moved successfully!`, type: 'success' }));
          
          setModalOpen(false);
          handleClose();
          
          // Navigate to target document's revision page
          const docType = targetDoc.type || 'Document';
          navigate(
            `/revision-submission/${targetDoc.partId}/${targetDoc.revision}/${docType}/${targetDoc.partNumber}/${targetDoc.revisionId}/no-description`);
        }

        return; // Exit early for child node handling
      }

      // Handle section move/copy (existing logic)
      // Find source section
      const sourceSection = sourceAllNodes.find(
        section => section.tree_node_id === moveNode.nodeId
      );

      if (!sourceSection) {
        dispatch(showSnackbar({ message: 'Source section not found.', type: 'error' }));
        dispatch(setMoveLoading(false));
        return;
      }

      // Calculate move parameters using utility function
      let moveParams;
      try {
        moveParams = calculateMoveParameters({
          position: movePosition.position,
          targetAllNodes,
          targetDoc,
          movePosition,
          moveCreateCopy,
          sourceSection, // Pass source section for order adjustment calculation
          moveDocument, // Pass move document to check if same-document move
        });
      } catch (error) {
        dispatch(showSnackbar({ message: error.message || 'Target section not found.', type: 'error' }));
        dispatch(setMoveLoading(false));
        return;
      }

      const { toOrder, toProcedureTreeNodeRef, toSectionIdRef, direction, direction_move_into } = moveParams;

      // Check if this is a copy operation
      if (moveCreateCopy) {
        // Prepare new content structure for version history
        const newContent = prepareCopyNewContent({
          targetAllNodes,
          sourceSection,
          targetDoc,
          position: movePosition.position,
          movePosition,
          toSectionIdRef,
        });

        // Build copy payload using utility function
        const copyPayload = await buildCopyPayload({
          sourceSection,
          moveDocument,
          targetDoc,
          position: movePosition.position,
          movePosition,
          toOrder,
          toProcedureTreeNodeRef,
          toSectionIdRef,
          direction,
          moveComment,
          targetAllNodesOriginal: targetAllNodes,
          newContent,
          sourceAllNodes,
        });

        const revAndTrainingData = await getRevAndTraining(dispatch, copyPayload.from_part_id_ref)

        const snapshot_url = await uploadJsonToS3({
          ...copyPayload.node_indexing,
          version_content:{
            action_to_show:copyPayload.audit_trail_data.version_content.action_to_show,
            item_code_type:targetDoc.type,
            partNumber:targetDoc.partNumber,
            status:targetDoc.status,
            revision:targetDoc.revision,
            partDescription: targetDoc.description,
            operation:"bulk_audit_trail",
            ...revAndTrainingData,
          }
        },targetDoc.partNumber,targetDoc.revision+"SECTION_COPIED",true)

        const current_snapshot = await uploadJsonToS3(
          prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }),
          targetDoc.partNumber,targetDoc.revision)
        
        const current_updated_snapshot = await uploadJsonToS3(
          prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }),
          targetDoc.partNumber,targetDoc.revision+"_current_updated_snapshot",true)

        Object.entries(copyPayload.node_indexing).forEach(([key, value]) => {
          copyPayload.node_indexing[key]={
            title:value.title,
            previous_content_snapshot:snapshot_url,
            new_content_snapshot:snapshot_url,
          }
        });
        copyPayload.current_snapshot = current_snapshot;
        copyPayload.current_updated_snapshot = current_updated_snapshot;
        copyPayload.audit_trail_data.previous_content_snapshot = snapshot_url
        copyPayload.audit_trail_data.new_content_snapshot = snapshot_url

        copyPayload.audit_trail_data.version_content = {
          ...copyPayload.audit_trail_data.version_content,
          main_entry: true,
          preview_tree_node_id: copyPayload.from_procedure_tree_node_ref,
        }
        // Make API call to copy section
        const response = await dispatch(copyElement(copyPayload)).unwrap();

        dispatch(showSnackbar({ message: 'Section copied successfully!', type: 'success' }));
        
        
        
        // Redirect to copied node if copy was successful
        const redirected = await handleRedirectToCopiedNode(
          response,
          targetDoc,
          dispatch,
          navigate,
          getRevAllNodes,
          setMoveLoading
        );

        setModalOpen(false);
        handleClose();

      } else {

        // Prepare new content structure for version history using utility function
        const newContent = prepareMoveNewContent({
          targetAllNodes,
          sourceSection,
          moveDocument,
          targetDoc,
          position: movePosition.position,
          movePosition,
          toSectionIdRef,
        });

        // Build move payload using utility function
        const movePayload = await buildMovePayload({
          sourceSection,
          moveDocument,
          targetDoc,
          position: movePosition.position,
          movePosition,
          moveNode,
          toOrder,
          toProcedureTreeNodeRef,
          toSectionIdRef,
          direction,
          direction_move_into,
          moveComment,
          targetAllNodesOriginal: targetAllNodes,
          sourceAllNodes,
          newContent,
        });



        const revAndTrainingData = await getRevAndTraining(dispatch, movePayload.from_part_id_ref)

        const snapshot_url = await uploadJsonToS3({
          ...movePayload.node_indexing,
          version_content:{
            action_to_show:movePayload.audit_trail_data.version_content.action_to_show,
            item_code_type:targetDoc.type,
            partNumber:targetDoc.partNumber,
            status:targetDoc.status,
            revision:targetDoc.revision,
            partDescription: targetDoc.description,
            operation:"bulk_audit_trail",
            ...revAndTrainingData,
          }
        },targetDoc.partNumber,targetDoc.revision+"SECTION_MOVED",true)

        const current_snapshot = await uploadJsonToS3(
          prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }),
          targetDoc.partNumber,targetDoc.revision)

        const current_updated_snapshot = await uploadJsonToS3(
          prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDoc.partNumber,
      procedure_rev: targetDoc.revision,
      procedure_status: targetDoc.status || 'Not Submitted',
      item_code_type: targetDoc.type,
      partDescription: targetDoc.description || "",
      part_ref: targetDoc.partId,
      procedure_rev_ref: targetDoc.revisionId
    }),
          targetDoc.partNumber,targetDoc.revision+"_current_updated_snapshot",true)

        Object.entries(movePayload.node_indexing).forEach(([key, value]) => {
          movePayload.node_indexing[key]={
            title:value.title,
            previous_content_snapshot:snapshot_url,
            new_content_snapshot:snapshot_url,
          }
        });


        movePayload.current_snapshot = current_snapshot;
        movePayload.current_updated_snapshot = current_updated_snapshot;
        movePayload.audit_trail_data.previous_content_snapshot = snapshot_url
        movePayload.audit_trail_data.new_content_snapshot = snapshot_url

        movePayload.audit_trail_data.version_content = {
          action_to_show:movePayload.audit_trail_data.version_content.action_to_show,
          main_entry:true,
          preview_tree_node_id:movePayload.from_procedure_tree_node_ref
        }
        // Make API call to move section
        
        await dispatch(moveSection(movePayload)).unwrap();
        
        dispatch(showSnackbar({ message: 'Section moved successfully!', type: 'success' }));
        
        setModalOpen(false);
        handleClose();
        
        // Navigate to target document's revision page
        // Use document details already fetched in NodeAppBar
        const docType = targetDoc.type || 'Document';
        const description = targetDoc.description || 'no-description';
        const encodedDescription = encodeURIComponent(description);
        navigate(
          `/revision-submission/${targetDoc.partId}/${targetDoc.revision}/${docType}/${targetDoc.partNumber}/${targetDoc.revisionId}/no-description`);
      }
    } catch (error) {
      console.error('Error moving section:', error);
      dispatch(showSnackbar({ message: 'Error moving section: ' + (error?.message || 'Unknown error'), type: 'error' }));
    } finally {
      dispatch(setMoveLoading(false));
    }
  };

  // Show loader during initial data fetch
  if (moveIsLoading && step === 'select' && !moveOriginalData) {
    return <Loader text="Loading document data..." />;
  }

  // Get step config
  const getStepConfig = () => {
    switch (step) {
      case 'select':
        return {
          title: `Moving ${moveNode?.nodeTitle || ''} from ${moveDocument?.partNumber} ${moveDocument?.revision}. Select the Target Document`,
          subheading: '',
          buttonsBeforeTitle: [
            { label: 'Cancel', onClick: handleClose }
          ],
          buttons: []
        };
      case 'position':
        // Title handled by MovePositionSelector
        return {
          title: '',
          subheading: '',
          buttonsBeforeTitle: [],
          buttons: []
        };
      case 'preview':
        return {
          title: 'Please Confirm the Changes',
          subheading: '',
          showCancelButton: true,
          onCancel: handleBack, // Go back to position selector instead of closing
          showViewToggle: true,
          selectedView: movePreviewMode,
          onViewChange: (mode) => handlePreviewModeChange(mode),
          buttonsBeforeTitle: [],
          buttons: [
            { label: 'Confirm', onClick: () => setModalOpen(true) }
          ]
        };
      default:
        return {
          title: 'Move',
          subheading: '',
          buttonsBeforeTitle: [],
          buttons: []
        };
    }
  };

  const stepConfig = getStepConfig();

  // Don't render anything until step is determined
  if (!step) {
    return <Loader text="Loading..." />;
  }

  return (
    <div>
      {/* Only render header if not in position step (handled by MovePositionSelector) */}
      {step !== 'position' && (
        <VersionHistoryCopyAppBar 
          title={stepConfig.title}
          subheading={stepConfig.subheading}
          buttons={stepConfig.buttons}
          buttonsBeforeTitle={stepConfig.buttonsBeforeTitle || []}
          showBackButton={false}
          showProfileButton={false}
          showTablesDropdown={false}
          showCancelButton={stepConfig.showCancelButton || false}
          onCancel={stepConfig.onCancel}
          showViewToggle={stepConfig.showViewToggle || false}
          selectedView={stepConfig.selectedView}
          onViewChange={stepConfig.onViewChange}
          showVersionInfo={true}
        />
      )}
      
      {/* Step 1: Select Target Document (only for cross-document moves) */}
      {step === 'select' && isCrossDocumentMove && (
        <div style={{ padding: '20px' }}>
          <SelectRevisionTable
            sourcePartNumber={moveDocument.partNumber}
            onRevisionSelect={handleRevisionSelect}
          />
        </div>
      )}

      {/* Step 2: Select Move Position */}
      {step === 'position' && moveDocument && (
        <MovePositionSelector
          onPositionSelected={handlePositionSelected}
          onCancel={handleClose}
          createCopy={moveCreateCopy}
          onCreateCopyChange={handleCreateCopyChange}
        />
      )}
      
      {/* Step 3: Preview */}
      {step === 'preview' && moveDocument && (
        <MoveReplacePreview
          moveDocument={moveDocument}
          moveNode={moveNode}
          moveTargetDocument={moveTargetDocument}
          movePosition={movePosition}
          previewMode={movePreviewMode}
          originalData={moveOriginalData}
          newData={moveNewData}
          onPreviewModeChange={handlePreviewModeChange}
          onConfirm={handleConfirmMove}
          comment={moveComment}
          onCommentChange={handleCommentChange}
          isLoading={moveIsLoading}
          isModalOpen={isModalOpen}
          onModalClose={() => setModalOpen(false)}
          moveCreateCopy={moveCreateCopy}
        />
      )}
    </div>
  );
};

export default MoveReplace;

